/**
 * @tt/lobby/cosmetics-catalog — Single source of truth for every cosmetic
 * in the game (lantern shop, season pass, achievement rewards, default
 * starters). Phase A of the season-lantern buildout: data model only.
 * Actual cosmetic designs are appended in Phase E without code changes
 * elsewhere.
 *
 * Server-side validation rule (see season-lantern-buildout.md §2.2):
 *   if `source.kind === 'season-premium'`, the item is EXCLUSIVE to that
 *   season's premium pass and MUST NOT appear in the lantern shop.
 */

/** Discriminated union describing how a cosmetic is acquired. */
export type CosmeticSource =
  | { kind: 'default' }
  | { kind: 'level'; level: number }
  | { kind: 'achievement'; id: string }
  | { kind: 'season-free'; season: number; tier: number }
  | { kind: 'season-premium'; season: number; tier: number }
  | { kind: 'lantern-shop'; price: number }
  | { kind: 'embers-shop'; price: number }
  | { kind: 'bundle'; bundleId: string };

/** Inventory bucket / wear-slot kinds covered by the cosmetic catalog. */
export type CosmeticKind =
  | 'body'
  | 'hat'
  | 'face'
  | 'accent'
  | 'palette'
  | 'border'
  | 'background'
  | 'particles'
  | 'title'
  // Phase F5 — seat-tile cosmetics. Parallel to (different from) the
  // avatar `border` / `background` / `particles` kinds: those decorate
  // the avatar icon itself, while these decorate the player's lobby
  // SEAT TILE chrome (frame around the tile, table backdrop, ambient
  // particles around the tile, and the optional bg hue overlay).
  // Registries live in `./presence.ts` (SEAT_BORDERS / SEAT_BACKGROUNDS
  // / SEAT_PARTICLES / SEAT_BG_HUES); inventory buckets in the
  // characters lambda are `seatBorders` / `seatBackgrounds` /
  // `seatParticles` / `seatBgHues`. Server-side unlock logic stays in
  // `deriveInventory` — this catalog only adds metadata for shop /
  // locker rendering.
  | 'seat-border'
  | 'seat-background'
  | 'seat-particle'
  | 'seat-bghue'
  // Phase F5 — masks. A new face-overlay cosmetic family rendered on
  // top of the avatar's face but UNDER the hat (z-order: background →
  // body → face → MASK → hat → border). Inventory bucket `masks` holds
  // the semantic registry id from `web/src/features/characters/
  // characterParts.tsx` MASKS. Most are rare/expensive; two are
  // animated mythics. Catalog id format: `mask-${semanticId}`.
  | 'mask'
  // OUTFITS — clothing / armor layered on the character's BODY (torso /
  // arms / legs), NOT a new body shape (the head still carries the icon's
  // body-shape silhouette + face + hat + mask). Rendered in 3D by
  // shell-3d's `buildOutfit` (OUTFIT_IDS); the 2D avatar portrait does not
  // show it (3D-only, like body archetypes). Inventory bucket `outfits`
  // holds the bare semantic id (e.g. 'plate'); `icon.outfit` stores the
  // same. Catalog id format: `outfit-${semanticId}`.
  | 'outfit'
  // "Your Own Tavern" Phase 2 (docs/planning/Own-Tavern-Plan.md) — hall
  // decoration items for the host's persistent table. One kind per slot
  // FAMILY (mantel trophies + trophy-wall plaques are separate kinds even
  // though both display "trophies", since they're earned differently and
  // occupy structurally different slots — 3 independent pedestals vs. one
  // ordered array). Ids/rarities mirror shared/shell-3d/src/decor.ts's
  // DECOR_ITEMS and shared/lobby/src/decor-registry.ts's DECOR_INDEX —
  // keep all three in sync.
  | 'decor-hearth'
  | 'decor-mantel'
  | 'decor-plaque'
  | 'decor-bar-shelf'
  | 'decor-rug'
  | 'decor-banner'
  | 'decor-centerpiece'
  | 'decor-wreath'
  | 'decor-window'
  | 'decor-jukebox'
  // "Your Own Tavern" HEARTH COMPANION — a living animal placed by the fire.
  // Empty by default (no starter companion); all 4 are shop-only. Ids mirror
  // shared/shell-3d/src/decor.ts + decor-registry.ts (keep in sync).
  | 'decor-companion'
  // "Your Own Tavern" surface-style slots (floor / bar-counter / tables) —
  // see shared/shell-3d/src/decor.ts's SURFACE_STYLES and shared/lobby/src/
  // decor-registry.ts's DECOR_INDEX. Style swaps, not placed objects, but
  // same catalog/inventory/economy shape as the decor-* kinds above.
  | 'decor-floor'
  | 'decor-bar-counter'
  | 'decor-tables'
  // "Your Own Tavern" Phase B — JUKEBOX RECORDS. A collectible that gates a
  // requestable jukebox song; each maps to one @tt/audio Mood. Inventory
  // bucket `records` holds the bare semantic id (e.g. 'hex-pulse'); catalog
  // id is `record-${id}`. Registry (id/label/mood/rarity) lives in
  // shared/lobby/src/records.ts's RECORDS — keep ids/rarities in sync.
  | 'record';

export type CosmeticRarity = 1 | 2 | 3 | 4 | 5;

export interface CosmeticDef {
  id: string;
  kind: CosmeticKind;
  name: string;
  /** 1 = common … 5 = mythic. */
  rarity: CosmeticRarity;
  source: CosmeticSource;
  /** Optional tags for animation, style, or theme grouping. */
  tags?: string[];
  /** For seasonal items, the season number. After the season ends, the
   *  item is locked from new acquisition but stays in player inventory. */
  season?: number;
  /** Visual-duplicate signature. Two catalog entries of the SAME `kind`
   *  MUST NOT share a `dupeKey` — that would mean two differently-named
   *  cosmetics render the same look (the avatar-background problem the
   *  economy pass consolidated). `tools/validate-catalog.mjs` enforces
   *  uniqueness. New cosmetics in a family that already declares dupeKeys
   *  (currently avatar `background`) must add a fresh, unique key. */
  dupeKey?: string;
}

export interface BundleDef {
  id: string;
  name: string;
  /** Cosmetic ids contained in the bundle. */
  contents: string[];
  /** Price in lanterns. */
  price: number;
  rarity: CosmeticRarity;
}

/** Master catalog. Phase E1 fills it with every existing cosmetic
 *  (bodies, hats, faces, hue accents, named palettes) tagged with its
 *  acquisition source. New items added in E2/E3 are appended here.
 *
 *  ID convention:
 *    body-${idx}    existing body shape / archetype (idx = inventory id)
 *    hat-${idx}     existing hat shape
 *    face-${idx}    existing face preset
 *    accent-${hue}  hue accent (numeric hue value)
 *    palette-${name} named 3-color palette
 *    Plus E2/E3 semantic ids (`solstice-bearer-body`, `ember-ring`, …).
 *
 *  Source mapping rules used below (cross-checked against
 *  `lambdas/characters/src/index.ts` deriveInventory + `shared/lobby/src/unlocks.ts`
 *  + `shared/achievements/src/index.ts` + `shared/lobby/src/shop.ts`):
 *    - body-0/1, hat-0, face-0/1/2, accent-0/25/50/180/250 → DEFAULT_INVENTORY
 *      starter set (`{ kind: 'default' }`).
 *    - Items unlocked through play thresholds (wins/matches/totalPoints/
 *      streak/setWins/mvpAwards) are tagged `level: N` as a forward-
 *      compatible proxy for the account-level system. The N values
 *      track rarity tiers, not exact thresholds — the server still
 *      gates on the underlying stat in deriveInventory.
 *    - Items unlocked by an achievement get `achievement: id` (matches
 *      the V2 achievement code, e.g. 'win-1', 'streak-3').
 *    - Hue / palette items currently in EMBERS_SHOP keep their existing
 *      embers-shop economy reference (`embers-shop` with price). Other
 *      hues that fall outside the play-unlock + default set are also
 *      embers-shop per the existing soft-currency design.
 *
 *  E2/E3 will append:
 *    ~10 lantern-shop bodies + 6 hats + 4 faces + 4 accents (24 new),
 *    6 borders + 4 backgrounds (10 new) — total ~34 new entries.
 *    Crimson Solstice five-piece (season-premium, season=1, rarity 5).
 */
export const COSMETIC_CATALOG: CosmeticDef[] = [
  // ── Bodies — id ${arrayIndex} = inventory.bodies value ──────────────
  // Phase F-Foundation: catalog id `body-N` matches BODY_SHAPES[N]. The
  // numeric index that's stored in inventory.bodies is exactly N. Walk
  // BODY_SHAPES in characterParts.tsx for the shape definitions.
  // Defaults — every fresh CHAR row starts with bodies [0, 1].
  { id: 'body-0',  kind: 'body', name: 'Blob',            rarity: 1, source: { kind: 'default' } },
  { id: 'body-1',  kind: 'body', name: 'Tall',            rarity: 1, source: { kind: 'default' } },
  // Play-unlocks (deriveInventory + unlocks.ts thresholds). `level` is a
  // proxy: the underlying gate is matches/wins/totalPoints, but the user-
  // facing source kind groups them together as "earned through play."
  { id: 'body-2',  kind: 'body', name: 'Cat',             rarity: 2, source: { kind: 'level', level: 5 } },
  { id: 'body-3',  kind: 'body', name: 'Bear',            rarity: 2, source: { kind: 'level', level: 8 } },
  { id: 'body-4',  kind: 'body', name: 'Pumpkin',         rarity: 2, source: { kind: 'level', level: 10 } },
  // Achievement-grants — direct rewards from V2 achievement entries.
  { id: 'body-5',  kind: 'body', name: 'Ghost',           rarity: 3, source: { kind: 'achievement', id: 'win-1' } },
  { id: 'body-6',  kind: 'body', name: 'Robot',           rarity: 2, source: { kind: 'level', level: 15 } },
  { id: 'body-7',  kind: 'body', name: 'Mushroom',        rarity: 2, source: { kind: 'level', level: 12 } },
  // Phase F-Foundation: index 8 was previously labeled "Frog" (E1) but
  // E3 inserted Solstice Bearer here. The catalog id stays `body-8`;
  // the name/source reflect what BODY_SHAPES[8] actually renders.
  { id: 'body-8',  kind: 'body', name: 'Solstice Bearer', rarity: 5, source: { kind: 'season-premium', season: 1, tier: 20 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  // Phase F-Foundation: indices 9-18 are the E2 new bodies. E2's earlier
  // semantic ids (`fox-body`, `wolf-body`, …) were renamed to the
  // numeric `body-${idx}` form. The original achievement-source body-9
  // (Owl), body-10 (Crystal), body-11 (Skull) entries are gone — those
  // ids now refer to BODY_SHAPES[9..11] which are Fox, Wolf, Raven.
  // The runtime achievement code in shared/lobby/src/unlocks.ts +
  // lambdas/leaderboards still grants inventory.bodies = [9..11] for
  // the original achievements; players who hit those thresholds now
  // receive the visually-different but inventorily-equivalent
  // Fox/Wolf/Raven cosmetic. Existing inventory data is unchanged.
  { id: 'body-9',  kind: 'body', name: 'Fox',             rarity: 3, source: { kind: 'season-free', season: 1, tier: 28 }, tags: ['animal', 'forest'] },
  { id: 'body-10', kind: 'body', name: 'Wolf',            rarity: 3, source: { kind: 'season-free', season: 1, tier: 31 }, tags: ['animal', 'forest'] },
  { id: 'body-11', kind: 'body', name: 'Raven',           rarity: 3, source: { kind: 'season-free', season: 1, tier: 37 }, tags: ['animal', 'arcane'] },
  // Phase F5 reprice — rarity bands narrowed so commons feel cheap
  // (75-150) and only mythics breach 1,500. r2 ≈ 300, r3 ≈ 600, r4 ≈ 1000.
  { id: 'body-12', kind: 'body', name: 'Owl Form',        rarity: 3, source: { kind: 'lantern-shop', price: 600 },          tags: ['animal', 'arcane'] },
  { id: 'body-13', kind: 'body', name: 'Jellyfish',       rarity: 4, source: { kind: 'season-free', season: 1, tier: 41 }, tags: ['animal', 'aquatic', 'animated'] },
  { id: 'body-14', kind: 'body', name: 'Living Candle',   rarity: 3, source: { kind: 'lantern-shop', price: 550 },          tags: ['hearth', 'animated'] },
  { id: 'body-15', kind: 'body', name: 'Lantern Form',    rarity: 3, source: { kind: 'lantern-shop', price: 600 },          tags: ['hearth', 'animated'] },
  { id: 'body-16', kind: 'body', name: 'Witch Broom',     rarity: 2, source: { kind: 'lantern-shop', price: 280 },          tags: ['arcane'] },
  { id: 'body-17', kind: 'body', name: 'Cauldron',        rarity: 3, source: { kind: 'lantern-shop', price: 650 },          tags: ['arcane', 'hearth'] },
  { id: 'body-18', kind: 'body', name: 'Scroll',          rarity: 2, source: { kind: 'lantern-shop', price: 250 },          tags: ['arcane', 'paper'] },
  // ── Body archetype IDs (100+) — sparse range used by V2 achievements
  // for "archetype" body rewards. Stored as the literal numeric value
  // (100, 101, 102, 104, 105) in inventory.bodies. There is no
  // BODY_SHAPES[100] entry — the renderer falls back to
  // bodyIdx % BODY_SHAPES.length, so the visual is currently shared
  // with another body. Catalog id convention is still
  // `body-${inventoryValue}`.
  { id: 'body-100', kind: 'body', name: 'Floating Orb',   rarity: 3, source: { kind: 'achievement', id: 'play-200' } },
  { id: 'body-101', kind: 'body', name: 'Eight-Legged',   rarity: 3, source: { kind: 'achievement', id: 'win-each-game' } },
  { id: 'body-102', kind: 'body', name: 'Phantom',        rarity: 4, source: { kind: 'achievement', id: 'streak-10' } },
  { id: 'body-104', kind: 'body', name: 'Living Flame',   rarity: 4, source: { kind: 'achievement', id: 'dawn-streak' } },
  // Was mis-cited as 'win-each-game' (already body-101's citation) — the
  // reward that actually grants this body is hexagon-heat's perGameMilestone
  // entry in ACHIEVEMENTS_V2 (code 'hexagon-heat-5', label "Hex Master").
  { id: 'body-105', kind: 'body', name: 'Hexagon',        rarity: 4, source: { kind: 'achievement', id: 'hexagon-heat-5' } },

  // ── Hats — id ${arrayIndex} = inventory.hats value ──────────────────
  // Phase F-Foundation: catalog id `hat-N` matches HAT_SHAPES[N].
  { id: 'hat-0',  kind: 'hat',  name: 'None',             rarity: 1, source: { kind: 'default' } },
  { id: 'hat-1',  kind: 'hat',  name: 'Wizard',           rarity: 2, source: { kind: 'achievement', id: 'win-1' } },
  { id: 'hat-2',  kind: 'hat',  name: 'Crown',            rarity: 3, source: { kind: 'level', level: 12 } },
  { id: 'hat-3',  kind: 'hat',  name: 'Top hat',          rarity: 2, source: { kind: 'level', level: 6 } },
  { id: 'hat-4',  kind: 'hat',  name: 'Hood',             rarity: 2, source: { kind: 'level', level: 8 } },
  { id: 'hat-5',  kind: 'hat',  name: 'Antlers',          rarity: 3, source: { kind: 'achievement', id: 'streak-3' } },
  { id: 'hat-6',  kind: 'hat',  name: 'Flower',           rarity: 2, source: { kind: 'level', level: 10 } },
  { id: 'hat-7',  kind: 'hat',  name: 'Candle',           rarity: 3, source: { kind: 'level', level: 18 } },
  // Phase F-Foundation: index 8 = Crown of Coals (E3 inserted),
  // indices 9-14 = E2 new hats. Renamed from semantic ids
  // (`crown-of-coals`, `tricorn-hat`, …) to numeric `hat-${idx}`.
  { id: 'hat-8',  kind: 'hat',  name: 'Crown of Coals',   rarity: 5, source: { kind: 'season-premium', season: 1, tier: 40 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  { id: 'hat-9',  kind: 'hat',  name: 'Tricorn',          rarity: 3, source: { kind: 'season-free', season: 1, tier: 29 }, tags: ['pirate'] },
  { id: 'hat-10', kind: 'hat',  name: 'Witch Hat',        rarity: 3, source: { kind: 'season-free', season: 1, tier: 32 }, tags: ['arcane'] },
  { id: 'hat-11', kind: 'hat',  name: 'Brass Goggles',    rarity: 3, source: { kind: 'lantern-shop', price: 600 },          tags: ['steampunk'] },
  { id: 'hat-12', kind: 'hat',  name: 'Antler Circlet',   rarity: 3, source: { kind: 'lantern-shop', price: 650 },          tags: ['forest', 'animal'] },
  { id: 'hat-13', kind: 'hat',  name: 'Halo',             rarity: 4, source: { kind: 'season-premium', season: 1, tier: 47 }, tags: ['celestial', 'animated'], season: 1 },
  { id: 'hat-14', kind: 'hat',  name: 'Candle Cap',       rarity: 2, source: { kind: 'lantern-shop', price: 250 },          tags: ['hearth', 'animated'] },
  // Iron Helm — HAT_SHAPES[15] / buildHat case 15. Open-face barbute so the
  // wearer's face still reads under the steel dome (like the other hats it must
  // not umbrella the head from the top-down camera). Flagship piece of the
  // Knight's Armory set. lantern-shop so it's individually buyable + bundleable.
  { id: 'hat-15', kind: 'hat',  name: 'Iron Helm',        rarity: 4, source: { kind: 'lantern-shop', price: 900 },          tags: ['knight', 'armor'] },

  // ── Faces — id ${arrayIndex} = inventory.faces value ────────────────
  // Phase F-Foundation: catalog id `face-N` matches FACE_SHAPES[N].
  { id: 'face-0', kind: 'face', name: 'Happy',            rarity: 1, source: { kind: 'default' } },
  { id: 'face-1', kind: 'face', name: 'Sleepy',           rarity: 1, source: { kind: 'default' } },
  { id: 'face-2', kind: 'face', name: 'Wide',             rarity: 1, source: { kind: 'default' } },
  { id: 'face-3', kind: 'face', name: 'Stern',            rarity: 2, source: { kind: 'level', level: 4 } },
  { id: 'face-4', kind: 'face', name: 'Cute',             rarity: 2, source: { kind: 'level', level: 6 } },
  { id: 'face-5', kind: 'face', name: 'Mischief',         rarity: 2, source: { kind: 'level', level: 9 } },
  { id: 'face-6', kind: 'face', name: 'Sad',              rarity: 2, source: { kind: 'level', level: 11 } },
  { id: 'face-7', kind: 'face', name: 'Cyclops',          rarity: 3, source: { kind: 'achievement', id: 'club-1k' } },
  // Phase F-Foundation: index 8 = Searing Mark (E3), indices 9-12 = E2
  // new faces. Renamed from semantic ids (`searing-mark`, `single-eye-
  // face`, …) to numeric `face-${idx}`.
  { id: 'face-8', kind: 'face', name: 'Searing Mark',     rarity: 5, source: { kind: 'season-premium', season: 1, tier: 60 }, tags: ['animated', 'crimson-solstice', 'mythic'], season: 1 },
  { id: 'face-9', kind: 'face', name: 'Single Eye',       rarity: 3, source: { kind: 'lantern-shop', price: 550 },          tags: ['cyclops'] },
  { id: 'face-10', kind: 'face', name: 'Third Eye',       rarity: 4, source: { kind: 'season-free', season: 1, tier: 34 }, tags: ['arcane'] },
  { id: 'face-11', kind: 'face', name: 'Calligraphic',    rarity: 3, source: { kind: 'lantern-shop', price: 500 },          tags: ['ink'] },
  { id: 'face-12', kind: 'face', name: 'Ghost Veil',      rarity: 4, source: { kind: 'season-premium', season: 1, tier: 8 },  tags: ['ghostly', 'animated'], season: 1 },

  // ── Hue accents (12) — mapped to existing embers-shop economy ──────
  // The 4 named palettes are the "feature" accent items; the other 8
  // are hue swatches that already live in EMBERS_SHOP. Per the existing
  // shop catalog (shared/lobby/src/shop.ts) prices are 300-1000 embers.
  { id: 'palette-dusk',      kind: 'palette', name: 'Dusk palette',      rarity: 2, source: { kind: 'default' }, tags: ['palette', 'starter'] },
  { id: 'palette-bone',      kind: 'palette', name: 'Bone palette',      rarity: 3, source: { kind: 'achievement', id: 'play-50' } },
  // Was mis-cited as 'witching-hour' (a HIDDEN achievement with no reward
  // of its own) — the reward that actually grants this palette is
  // ouija-roulette's perGameMilestone entry in ACHIEVEMENTS_V2 (code
  // 'ouija-roulette-5', label "Spirit Speaker").
  { id: 'palette-emerald',   kind: 'palette', name: 'Emerald palette',   rarity: 3, source: { kind: 'achievement', id: 'ouija-roulette-5' } },
  // Economy pass: Prismatic is an animated rarity-4 palette — a premium
  // look that never fit the "common cosmetics" Embers shop (which caps at
  // rarity 3). Moved to the LANTERN shop so the store split holds (Lantern
  // = purchasable premium, Embers = earnable commons). r4 lantern band ≈ 1000.
  { id: 'palette-prismatic', kind: 'palette', name: 'Prismatic palette', rarity: 4, source: { kind: 'lantern-shop', price: 1000 }, tags: ['palette', 'animated'] },
  // ── Themed set palettes — the "matching-set" glue for the armory bundles.
  // Cloth outfits (tunic / cloak / robe) tint to the palette body color in 3D
  // (shell-3d buildOutfit shadeHex(primary)), so pairing a set's palette with
  // its outfit + hat + aura + backdrop reads as ONE coherent look. All four are
  // lantern-shop so they're individually buyable AND bundleable (never
  // season-premium — bundle exclusivity guarantee holds). Color triples added
  // to PALETTES (character-render + shell-3d), PALETTE_HSL (IconAvatar), and the
  // locker / picker swatch lists. Priced r3 ≈ 550 to match the F5 band.
  { id: 'palette-steel',     kind: 'palette', name: 'Steel palette',     rarity: 3, source: { kind: 'lantern-shop', price: 550 }, tags: ['palette', 'knight', 'armor'] },
  { id: 'palette-arcane',    kind: 'palette', name: 'Arcane palette',    rarity: 3, source: { kind: 'lantern-shop', price: 550 }, tags: ['palette', 'arcane', 'mage'] },
  { id: 'palette-royal',     kind: 'palette', name: 'Royal palette',     rarity: 3, source: { kind: 'lantern-shop', price: 550 }, tags: ['palette', 'royal', 'gold'] },
  { id: 'palette-naval',     kind: 'palette', name: 'Naval palette',     rarity: 3, source: { kind: 'lantern-shop', price: 550 }, tags: ['palette', 'pirate', 'naval'] },
  // Economy pass: catalog embers prices realigned to the LIVE Embers shop
  // (shared/lobby/src/shop.ts) after the R2#74 doubling — they were stale
  // pre-#74 values, so the catalog "declared" a price the shop never charged.
  { id: 'accent-50',         kind: 'accent',  name: 'Honey hue',         rarity: 1, source: { kind: 'embers-shop', price: 300 } },
  { id: 'accent-110',        kind: 'accent',  name: 'Mint hue',          rarity: 1, source: { kind: 'embers-shop', price: 300 } },
  { id: 'accent-240',        kind: 'accent',  name: 'Indigo hue',        rarity: 1, source: { kind: 'embers-shop', price: 400 } },
  { id: 'accent-320',        kind: 'accent',  name: 'Orchid hue',        rarity: 1, source: { kind: 'embers-shop', price: 400 } },
  { id: 'accent-5',          kind: 'accent',  name: 'Rose petal hue',    rarity: 1, source: { kind: 'embers-shop', price: 300 } },
  { id: 'accent-195',        kind: 'accent',  name: 'Aqua hue',          rarity: 1, source: { kind: 'embers-shop', price: 350 } },
  { id: 'accent-75',         kind: 'accent',  name: 'Marigold hue',      rarity: 1, source: { kind: 'embers-shop', price: 400 } },
  { id: 'accent-280',        kind: 'accent',  name: 'Grape hue',         rarity: 2, source: { kind: 'embers-shop', price: 600 } },

  // ── Borders (6) — Phase E3 ─────────────────────────────────────────
  // 5 lantern-shop entries (250-1500 lanterns by rarity) + ember-ring
  // (Crimson Solstice tier 30 premium-only). Render code lives in
  // BORDERS in web/src/features/characters/characterParts.tsx.
  { id: 'leaf-ring',         kind: 'border', name: 'Leaf Ring',           rarity: 2, source: { kind: 'lantern-shop', price: 280 } },
  { id: 'brass-frame',       kind: 'border', name: 'Brass Frame',         rarity: 2, source: { kind: 'lantern-shop', price: 320 } },
  { id: 'candle-circle',     kind: 'border', name: 'Candle Circle',       rarity: 3, source: { kind: 'lantern-shop', price: 600 }, tags: ['animated'] },
  { id: 'gemstone-studded',  kind: 'border', name: 'Gemstone Studded',    rarity: 3, source: { kind: 'lantern-shop', price: 650 } },
  { id: 'runic-glyph',       kind: 'border', name: 'Runic Glyph',         rarity: 4, source: { kind: 'lantern-shop', price: 1000 }, tags: ['animated'] },
  { id: 'ember-ring',        kind: 'border', name: 'Ember Ring',          rarity: 5, source: { kind: 'season-premium', season: 1, tier: 30 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  // Phase F6 — 6 NEW avatar borders. F5 only added new SEAT borders;
  // the avatar Border slot was stuck at the original 6 (1 of which is
  // premium-locked). These six widen the equip menu so non-premium
  // players have actual variety to pick from. Animation policy follows
  // F5 (rarity 3 subtle, rarity 4-5 layered). Render code lives in
  // BORDERS in `web/src/features/characters/characterParts.tsx`.
  { id: 'obsidian-band',     kind: 'border', name: 'Obsidian Band',       rarity: 3, source: { kind: 'lantern-shop', price: 600 }, tags: ['volcanic'] },
  { id: 'crystal-shard',     kind: 'border', name: 'Crystal Shard',       rarity: 4, source: { kind: 'lantern-shop', price: 1100 }, tags: ['crystal', 'animated'] },
  { id: 'tide-foam-ring',    kind: 'border', name: 'Tide Foam Ring',      rarity: 3, source: { kind: 'lantern-shop', price: 650 }, tags: ['water', 'animated'] },
  { id: 'void-eclipse',      kind: 'border', name: 'Void Eclipse',        rarity: 5, source: { kind: 'lantern-shop', price: 2000 }, tags: ['cosmic', 'animated'] },
  { id: 'phoenix-feather',   kind: 'border', name: 'Phoenix Feather',     rarity: 4, source: { kind: 'season-free', season: 1, tier: 26 }, tags: ['animated'] },
  { id: 'gilded-rose-ring',  kind: 'border', name: 'Gilded Rose Ring',    rarity: 4, source: { kind: 'season-free', season: 1, tier: 17 }, tags: ['gold'] },

  // ── Backgrounds (4) — Phase E3 ─────────────────────────────────────
  // 3 lantern-shop + forges-maw (Crimson Solstice tier 50 premium-only).
  // Render code lives in BACKGROUNDS in characterParts.tsx.
  // Economy pass: avatar backgrounds now carry a `dupeKey` (visual-family
  // signature). `cosmos-veil` (below) was a near-identical twin of
  // `starfield` — both a deep indigo/violet void with multi-depth twinkling
  // stars, rotating nebulae, and a fractalNoise cosmic-dust overlay. They
  // were CONSOLIDATED into the canonical `starfield`; `cosmos-veil` is
  // removed from the economy catalog (its render entry stays in the
  // BACKGROUNDS registry so legacy wearers still render, and ownership is
  // migrated to `starfield` union-on-read in lambdas/characters
  // deriveInventory). validate-catalog.mjs enforces one entry per dupeKey.
  { id: 'tavern-hearth',     kind: 'background', name: 'Tavern Hearth',   rarity: 2, source: { kind: 'lantern-shop', price: 320 },  dupeKey: 'hearth' },
  { id: 'forest-grove',      kind: 'background', name: 'Forest Grove',    rarity: 3, source: { kind: 'lantern-shop', price: 600 }, tags: ['animated'], dupeKey: 'forest' },
  { id: 'starfield',         kind: 'background', name: 'Starfield',       rarity: 4, source: { kind: 'lantern-shop', price: 1000 }, tags: ['animated', 'cosmic'], dupeKey: 'cosmic' },
  { id: 'forges-maw',        kind: 'background', name: "Forge's Maw",     rarity: 5, source: { kind: 'season-premium', season: 1, tier: 50 }, tags: ['animated', 'crimson-solstice'], season: 1, dupeKey: 'forge' },
  // Phase F6 — 3 NEW avatar backgrounds (was 4; cosmos-veil consolidated
  // into starfield). Same gap-fill as the borders: F5 only added SEAT
  // backgrounds. Render code lives in BACKGROUNDS in
  // `web/src/features/characters/characterParts.tsx`.
  { id: 'parchment-scroll',  kind: 'background', name: 'Parchment Scroll',rarity: 2, source: { kind: 'embers-shop', price: 400 },  dupeKey: 'parchment' },
  { id: 'velvet-drape',      kind: 'background', name: 'Velvet Drape',    rarity: 3, source: { kind: 'lantern-shop', price: 600 },  dupeKey: 'velvet' },
  { id: 'tide-pool-wash',    kind: 'background', name: 'Tide Pool Wash',  rarity: 3, source: { kind: 'lantern-shop', price: 650 }, tags: ['water', 'animated'], dupeKey: 'tide' },

  // ── Phase E2 — new accent hues (4) ─────────────────────────────────
  // 2 embers-shop (existing soft-currency lane), 2 lantern-shop. Hue
  // numbers extend the HUES array in characterParts.tsx.
  { id: 'accent-18',  kind: 'accent', name: 'Sunset hue',   rarity: 2, source: { kind: 'embers-shop', price: 600 }, tags: ['warm'] },
  { id: 'accent-35',  kind: 'accent', name: 'Bone hue',     rarity: 2, source: { kind: 'embers-shop', price: 600 }, tags: ['neutral'] },
  { id: 'accent-130', kind: 'accent', name: 'Forest hue',   rarity: 2, source: { kind: 'lantern-shop', price: 220 }, tags: ['nature'] },
  { id: 'accent-270', kind: 'accent', name: 'Twilight hue', rarity: 3, source: { kind: 'lantern-shop', price: 480 }, tags: ['arcane'] },

  // ── Seat — Borders (6) — Phase F5 ─────────────────────────────────
  // SEAT_BORDERS in `./presence.ts`. Server unlock logic in
  // `deriveInventory`:
  //   ember      ← win-1 achievement
  //   frost      ← matches ≥ 3            (level proxy)
  //   vine       ← wins ≥ 5               (level proxy)
  //   storm      ← totalPoints ≥ 1000     (level proxy)
  //   champion   ← setWins ≥ 3            (level proxy)
  // The numeric `level` values below are forward-compatible proxies for
  // the stat-threshold gates — same convention as the body/hat/face
  // catalog entries above.
  { id: 'seat-border-none',     kind: 'seat-border', name: 'Plain frame',    rarity: 1, source: { kind: 'default' } },
  { id: 'seat-border-ember',    kind: 'seat-border', name: 'Ember frame',    rarity: 2, source: { kind: 'achievement', id: 'win-1' } },
  { id: 'seat-border-frost',    kind: 'seat-border', name: 'Frost frame',    rarity: 2, source: { kind: 'level', level: 3 } },
  { id: 'seat-border-vine',     kind: 'seat-border', name: 'Vine frame',     rarity: 2, source: { kind: 'level', level: 6 } },
  { id: 'seat-border-storm',    kind: 'seat-border', name: 'Storm frame',    rarity: 3, source: { kind: 'level', level: 14 } },
  { id: 'seat-border-champion', kind: 'seat-border', name: 'Champion frame', rarity: 4, source: { kind: 'level', level: 20 } },

  // ── Seat — Backgrounds (6) — Phase F5 ─────────────────────────────
  // SEAT_BACKGROUNDS in `./presence.ts`. Server unlock logic:
  //   plain     ← default
  //   tavern    ← default
  //   moon      ← daily streak ≥ 3
  //   spice     ← spice-rush plays ≥ 3   (gameplay-specific stat)
  //   lantern   ← wins ≥ 10
  //   arcane    ← mvpAwards ≥ 5
  // TODO: `spice` is gated on a per-game stat (spice-rush plays),
  // not directly mappable to a generic level/achievement source.
  // `level: 8` is a rough proxy; consider adding a
  // `{ kind: 'play-game', gameId: 'spice-rush', count: 3 }` source
  // type in a future pass for accurate hint copy.
  { id: 'seat-background-plain',   kind: 'seat-background', name: 'Plain table',   rarity: 1, source: { kind: 'default' } },
  { id: 'seat-background-tavern',  kind: 'seat-background', name: 'Tavern glow',   rarity: 1, source: { kind: 'default' } },
  { id: 'seat-background-moon',    kind: 'seat-background', name: 'Moonlit booth', rarity: 2, source: { kind: 'level', level: 5 } },
  { id: 'seat-background-spice',   kind: 'seat-background', name: 'Spice rush',    rarity: 3, source: { kind: 'level', level: 8 } },
  { id: 'seat-background-lantern', kind: 'seat-background', name: 'Lantern light', rarity: 3, source: { kind: 'level', level: 12 } },
  { id: 'seat-background-arcane',  kind: 'seat-background', name: 'Arcane circle', rarity: 4, source: { kind: 'level', level: 18 } },

  // ── Seat — Particles (6) — Phase F5 ───────────────────────────────
  // SEAT_PARTICLES in `./presence.ts`. Server unlock logic:
  //   none       ← default
  //   embers     ← win-1 achievement
  //   snow       ← matches ≥ 5      (level proxy)
  //   sparks     ← wins ≥ 7         (level proxy)
  //   cards      ← setWins ≥ 1      (level proxy)
  //   fireflies  ← streak ≥ 5       (level proxy)
  { id: 'seat-particle-none',      kind: 'seat-particle', name: 'No particles',  rarity: 1, source: { kind: 'default' } },
  { id: 'seat-particle-embers',    kind: 'seat-particle', name: 'Embers',        rarity: 2, source: { kind: 'achievement', id: 'win-1' } },
  { id: 'seat-particle-snow',      kind: 'seat-particle', name: 'Snow drift',    rarity: 2, source: { kind: 'level', level: 4 } },
  { id: 'seat-particle-sparks',    kind: 'seat-particle', name: 'Gold sparks',   rarity: 3, source: { kind: 'level', level: 9 } },
  { id: 'seat-particle-cards',     kind: 'seat-particle', name: 'Card shimmer',  rarity: 3, source: { kind: 'level', level: 7 } },
  { id: 'seat-particle-fireflies', kind: 'seat-particle', name: 'Fireflies',     rarity: 3, source: { kind: 'level', level: 10 } },

  // ── Seat — Background hues (10) — Phase F5 ────────────────────────
  // SEAT_BG_HUES in `./presence.ts`. Server unlock logic in
  // `deriveInventory` is a mix of defaults + stat thresholds. Numeric
  // ids match the CSS `data-bg-hue` attribute the SeatPreview emits.
  //   25  ← default     (Ember)
  //   210 ← default     (Azure)
  //   290 ← default     (Velvet)
  //   0   ← matches ≥ 2          (level proxy)
  //   50  ← totalPoints ≥ 100    (level proxy)
  //   90  ← matches ≥ 8          (level proxy)
  //   140 ← wins ≥ 3             (level proxy)
  //   180 ← wins ≥ 6             (level proxy)
  //   250 ← totalPoints ≥ 500    (level proxy)
  //   330 ← mvpAwards ≥ 2        (level proxy)
  { id: 'seat-bghue-25',  kind: 'seat-bghue', name: 'Ember hue',   rarity: 1, source: { kind: 'default' } },
  { id: 'seat-bghue-210', kind: 'seat-bghue', name: 'Azure hue',   rarity: 1, source: { kind: 'default' } },
  { id: 'seat-bghue-290', kind: 'seat-bghue', name: 'Velvet hue',  rarity: 1, source: { kind: 'default' } },
  { id: 'seat-bghue-0',   kind: 'seat-bghue', name: 'Crimson hue', rarity: 2, source: { kind: 'level', level: 2 } },
  { id: 'seat-bghue-50',  kind: 'seat-bghue', name: 'Honey hue',   rarity: 2, source: { kind: 'level', level: 4 } },
  { id: 'seat-bghue-90',  kind: 'seat-bghue', name: 'Moss hue',    rarity: 2, source: { kind: 'level', level: 6 } },
  { id: 'seat-bghue-140', kind: 'seat-bghue', name: 'Forest hue',  rarity: 2, source: { kind: 'level', level: 5 } },
  { id: 'seat-bghue-180', kind: 'seat-bghue', name: 'Tide hue',    rarity: 2, source: { kind: 'level', level: 8 } },
  { id: 'seat-bghue-250', kind: 'seat-bghue', name: 'Indigo hue',  rarity: 3, source: { kind: 'level', level: 11 } },
  { id: 'seat-bghue-330', kind: 'seat-bghue', name: 'Rose hue',    rarity: 3, source: { kind: 'level', level: 9 } },

  // ── Phase F5 — NEW seat cosmetics ─────────────────────────────────
  // Animation policy (CSS-only): r1 static, r2 subtle 5-8s loop, r3
  // layered 3-5s loop, r4 multi-layer 2-3s, r5 dramatic exclusive
  // treatment. Selectors live in `web/src/styles/global.css` under the
  // seat-cosmetic section. Keep CSS animations cheap (transform /
  // opacity / filter only) — many seats render simultaneously.
  // Repriced under the F5 bands (r1 75-150, r2 200-350, r3 450-700,
  // r4 800-1200, r5 1500-2200 lanterns; embers commons 50-200 / mythic
  // 800-1500). The runtime unlock chain in `deriveInventory` does NOT
  // gate these new seat-* ids — they're earnable only via shop / season
  // (or default for seat-background-parchment). Players who don't buy
  // them just don't see them in inventory.
  { id: 'seat-border-obsidian',       kind: 'seat-border', name: 'Obsidian frame',       rarity: 3, source: { kind: 'lantern-shop', price: 600 }, tags: ['volcanic'] },
  { id: 'seat-border-gilded-rose',    kind: 'seat-border', name: 'Gilded Rose frame',    rarity: 4, source: { kind: 'season-free', season: 1, tier: 16 }, tags: ['gold', 'animated'] },
  { id: 'seat-border-wrought-iron',   kind: 'seat-border', name: 'Wrought Iron frame',   rarity: 2, source: { kind: 'embers-shop', price: 250 }, tags: ['iron'] },
  { id: 'seat-border-crystal-shard',  kind: 'seat-border', name: 'Crystal Shard frame',  rarity: 4, source: { kind: 'lantern-shop', price: 1100 }, tags: ['crystal', 'animated'] },
  { id: 'seat-border-solstice-flame', kind: 'seat-border', name: 'Solstice Flame frame', rarity: 5, source: { kind: 'season-premium', season: 1, tier: 21 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  { id: 'seat-border-runic-engraved', kind: 'seat-border', name: 'Runic Engraved frame', rarity: 3, source: { kind: 'achievement', id: 'witching-hour' }, tags: ['arcane', 'animated'] },
  { id: 'seat-border-tide-foam',      kind: 'seat-border', name: 'Tide Foam frame',      rarity: 3, source: { kind: 'season-free', season: 1, tier: 7 }, tags: ['water', 'animated'] },
  { id: 'seat-border-void-ring',      kind: 'seat-border', name: 'Void Ring frame',      rarity: 5, source: { kind: 'lantern-shop', price: 2000 }, tags: ['cosmic', 'animated'] },

  { id: 'seat-background-parchment',      kind: 'seat-background', name: 'Parchment',      rarity: 1, source: { kind: 'default' } },
  { id: 'seat-background-velvet-curtain', kind: 'seat-background', name: 'Velvet Curtain', rarity: 2, source: { kind: 'lantern-shop', price: 280 } },
  { id: 'seat-background-forge-glow',     kind: 'seat-background', name: 'Forge Glow',     rarity: 5, source: { kind: 'season-premium', season: 1, tier: 35 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  { id: 'seat-background-library',        kind: 'seat-background', name: 'Library',        rarity: 3, source: { kind: 'achievement', id: 'play-50' }, tags: ['animated'] },
  { id: 'seat-background-garden',         kind: 'seat-background', name: 'Garden',         rarity: 3, source: { kind: 'season-free', season: 1, tier: 8 }, tags: ['nature', 'animated'] },
  { id: 'seat-background-cosmos',         kind: 'seat-background', name: 'Cosmos',         rarity: 4, source: { kind: 'lantern-shop', price: 1100 }, tags: ['cosmic', 'animated'] },
  { id: 'seat-background-tide-pool',      kind: 'seat-background', name: 'Tide Pool',      rarity: 3, source: { kind: 'season-free', season: 1, tier: 11 }, tags: ['water', 'animated'] },
  { id: 'seat-background-eclipse',        kind: 'seat-background', name: 'Eclipse',        rarity: 5, source: { kind: 'lantern-shop', price: 2100 }, tags: ['cosmic', 'animated'] },

  { id: 'seat-particle-petals',           kind: 'seat-particle', name: 'Petals',           rarity: 2, source: { kind: 'embers-shop', price: 300 }, tags: ['petals'] },
  { id: 'seat-particle-bubbles',          kind: 'seat-particle', name: 'Bubbles',          rarity: 2, source: { kind: 'lantern-shop', price: 220 } },
  { id: 'seat-particle-lightning',        kind: 'seat-particle', name: 'Lightning',        rarity: 4, source: { kind: 'achievement', id: 'streak-10' }, tags: ['animated'] },
  { id: 'seat-particle-confetti',         kind: 'seat-particle', name: 'Confetti',         rarity: 3, source: { kind: 'season-free', season: 1, tier: 14 }, tags: ['animated'] },
  { id: 'seat-particle-runes',            kind: 'seat-particle', name: 'Runes',            rarity: 4, source: { kind: 'season-premium', season: 1, tier: 45 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  { id: 'seat-particle-phoenix-feathers', kind: 'seat-particle', name: 'Phoenix Feathers', rarity: 5, source: { kind: 'lantern-shop', price: 1900 }, tags: ['animated'] },
  { id: 'seat-particle-shadows',          kind: 'seat-particle', name: 'Shadows',          rarity: 4, source: { kind: 'achievement', id: 'streak-3' }, tags: ['animated'] },
  { id: 'seat-particle-coins',            kind: 'seat-particle', name: 'Coins',            rarity: 3, source: { kind: 'lantern-shop', price: 600 }, tags: ['gold', 'animated'] },

  // Phase F5 — new seat-bghue ids matching the SEAT_BG_HUES additions.
  // Static (no animation), so all r1/r2. Cheap embers-shop entries to
  // give body-color variety without flooding lanterns.
  { id: 'seat-bghue-15',  kind: 'seat-bghue', name: 'Burnt Sienna hue', rarity: 1, source: { kind: 'embers-shop', price: 80 } },
  { id: 'seat-bghue-45',  kind: 'seat-bghue', name: 'Mustard hue',      rarity: 1, source: { kind: 'embers-shop', price: 80 } },
  { id: 'seat-bghue-75',  kind: 'seat-bghue', name: 'Olive hue',        rarity: 1, source: { kind: 'embers-shop', price: 80 } },
  { id: 'seat-bghue-120', kind: 'seat-bghue', name: 'Pine hue',         rarity: 1, source: { kind: 'embers-shop', price: 100 } },
  { id: 'seat-bghue-200', kind: 'seat-bghue', name: 'Storm hue',        rarity: 2, source: { kind: 'embers-shop', price: 200 } },
  { id: 'seat-bghue-265', kind: 'seat-bghue', name: 'Periwinkle hue',   rarity: 2, source: { kind: 'embers-shop', price: 200 } },

  // ── Phase F5 — Masks (10) ─────────────────────────────────────────
  // New face-overlay cosmetic family. Most are rare/expensive; two are
  // animated mythics (`mask-mirror`, `mask-crimson-veil`). Inventory
  // bucket `masks` (string-id semantic). Render registry MASKS lives in
  // `web/src/features/characters/characterParts.tsx`. Catalog ids
  // follow the `mask-${semanticId}` pattern; resolver parses the
  // suffix into the inventory string verbatim. The five-piece Crimson
  // Solstice exclusivity is preserved — `mask-crimson-veil` is the
  // ONLY mask with `source.kind = 'season-premium'`.
  { id: 'mask-domino',        kind: 'mask', name: 'Domino mask',        rarity: 2, source: { kind: 'lantern-shop', price: 280 } },
  { id: 'mask-feline',        kind: 'mask', name: 'Feline mask',        rarity: 3, source: { kind: 'lantern-shop', price: 600 } },
  { id: 'mask-harlequin',     kind: 'mask', name: 'Harlequin mask',     rarity: 3, source: { kind: 'lantern-shop', price: 650 } },
  { id: 'mask-phantom',       kind: 'mask', name: 'Phantom mask',       rarity: 4, source: { kind: 'achievement', id: 'streak-3' }, tags: ['animated'] },
  { id: 'mask-oni',           kind: 'mask', name: 'Oni mask',           rarity: 4, source: { kind: 'season-free', season: 1, tier: 22 }, tags: ['demon'] },
  { id: 'mask-sunburst',      kind: 'mask', name: 'Sunburst mask',      rarity: 4, source: { kind: 'lantern-shop', price: 1100 }, tags: ['gold', 'animated'] },
  { id: 'mask-void',          kind: 'mask', name: 'Void mask',          rarity: 5, source: { kind: 'lantern-shop', price: 1900 }, tags: ['cosmic', 'animated'] },
  { id: 'mask-crimson-veil',  kind: 'mask', name: 'Crimson Veil mask',  rarity: 5, source: { kind: 'season-premium', season: 1, tier: 53 }, tags: ['animated', 'crimson-solstice'], season: 1 },
  { id: 'mask-mardi-gras',    kind: 'mask', name: 'Mardi Gras mask',    rarity: 3, source: { kind: 'season-free', season: 1, tier: 4 } },
  { id: 'mask-mirror',        kind: 'mask', name: 'Mirror mask',        rarity: 5, source: { kind: 'lantern-shop', price: 2200 }, tags: ['animated'] },

  // ── Outfits (6) — body clothing / armor (STARTER SET) ─────────────
  // New body-cosmetic family rendered in 3D by shell-3d's `buildOutfit`
  // (OUTFIT_IDS = tunic / jerkin / cloak / robe / plate / regalia). The
  // bare body (no outfit) is the default — fresh characters wear nothing
  // (no regression). Inventory bucket `outfits` stores the bare semantic
  // id (matches `icon.outfit`). Catalog id `outfit-${id}`; the resolver
  // parses the suffix into the inventory string verbatim (like masks).
  // Spread across rarities + sources so they flow through level unlocks,
  // the lantern shop, and the season pass. `outfit-regalia` is the ONLY
  // outfit with `source.kind = 'season-premium'` (Crimson Solstice
  // exclusivity preserved). More outfits can be appended later.
  { id: 'outfit-tunic',   kind: 'outfit', name: 'Tunic',               rarity: 1, source: { kind: 'default' } },
  { id: 'outfit-jerkin',  kind: 'outfit', name: 'Leather Jerkin',      rarity: 2, source: { kind: 'level', level: 7 } },
  { id: 'outfit-cloak',   kind: 'outfit', name: 'Hooded Cloak',        rarity: 3, source: { kind: 'achievement', id: 'streak-3' } },
  { id: 'outfit-robe',    kind: 'outfit', name: 'Mage Robe',           rarity: 3, source: { kind: 'lantern-shop', price: 650 }, tags: ['arcane'] },
  { id: 'outfit-plate',   kind: 'outfit', name: 'Plate Armor',         rarity: 4, source: { kind: 'lantern-shop', price: 1100 }, tags: ['armor'] },
  { id: 'outfit-regalia', kind: 'outfit', name: 'Emberforged Regalia', rarity: 5, source: { kind: 'season-premium', season: 1, tier: 23 }, tags: ['animated', 'crimson-solstice', 'mythic'], season: 1 },

  // ── "Your Own Tavern" decor (29) — Phase 2 (docs/planning/
  // Own-Tavern-Plan.md). Bare semantic ids (matches border/background
  // convention, not a `${kind}-` prefix) — see shared/lobby/src/
  // decor-registry.ts's DECOR_INDEX (keep ids/rarities in sync with both
  // that file and shared/shell-3d/src/decor.ts's DECOR_ITEMS). 4 defaults
  // (hearth/rug/window/jukebox) are `{kind:'default'}` — the pre-existing
  // hall look, so nobody regresses. Mantel trophies + trophy-wall plaques
  // are achievement-only, NEVER purchasable (earned, not bought). The rest
  // split lantern-shop (the bigger showpieces) / embers-shop (cheap
  // starter-tier fillers).
  // Prices cut ~40% (owner 2026-07-05: "pretty expensive") — see the
  // matching cut in shared/lobby/src/shop.ts's EMBERS_SHOP_DECOR.
  { id: 'ember-classic',   kind: 'decor-hearth',     name: 'Ember Classic',   rarity: 2, source: { kind: 'default' } },
  { id: 'witchfire',       kind: 'decor-hearth',     name: 'Witchfire',       rarity: 4, source: { kind: 'lantern-shop', price: 550 } },
  { id: 'candle-shrine',   kind: 'decor-hearth',     name: 'Candle Shrine',   rarity: 3, source: { kind: 'embers-shop', price: 300 } },

  { id: 'golden-tankard',  kind: 'decor-mantel',     name: 'Golden Tankard',  rarity: 3, source: { kind: 'achievement', id: 'set-1' } },
  { id: 'silver-rope',     kind: 'decor-mantel',     name: 'Silver Rope',     rarity: 3, source: { kind: 'achievement', id: 'rope-walk-5' } },
  { id: 'crystal-hex',     kind: 'decor-mantel',     name: 'Crystal Hex',     rarity: 4, source: { kind: 'achievement', id: 'hexagon-heat-5' } },
  { id: 'phoenix-cup',     kind: 'decor-mantel',     name: 'Phoenix Cup',     rarity: 5, source: { kind: 'achievement', id: 'set-25' } },

  { id: 'plaque-bronze',   kind: 'decor-plaque',     name: 'Bronze Plaque',   rarity: 1, source: { kind: 'achievement', id: 'play-10' } },
  { id: 'plaque-silver',   kind: 'decor-plaque',     name: 'Silver Plaque',   rarity: 2, source: { kind: 'achievement', id: 'win-10' } },
  { id: 'plaque-gold',     kind: 'decor-plaque',     name: 'Gold Plaque',     rarity: 3, source: { kind: 'achievement', id: 'set-5' } },

  { id: 'classic-bottles', kind: 'decor-bar-shelf',  name: 'Classic Bottles', rarity: 1, source: { kind: 'embers-shop', price: 150 } },
  { id: 'potion-rack',     kind: 'decor-bar-shelf',  name: 'Potion Rack',     rarity: 3, source: { kind: 'lantern-shop', price: 300 } },
  { id: 'cauldron-set',    kind: 'decor-bar-shelf',  name: 'Cauldron Set',    rarity: 4, source: { kind: 'lantern-shop', price: 500 } },

  { id: 'crimson-diamond', kind: 'decor-rug',        name: 'Crimson Diamond', rarity: 1, source: { kind: 'default' } },
  { id: 'midnight-star',   kind: 'decor-rug',        name: 'Midnight Star',   rarity: 3, source: { kind: 'lantern-shop', price: 300 } },
  { id: 'harvest-braid',   kind: 'decor-rug',        name: 'Harvest Braid',   rarity: 2, source: { kind: 'lantern-shop', price: 200 } },

  { id: 'pennant-string',  kind: 'decor-banner',     name: 'Pennant String',  rarity: 1, source: { kind: 'embers-shop', price: 100 } },
  { id: 'ember-tapestry',  kind: 'decor-banner',     name: 'Ember Tapestry',  rarity: 3, source: { kind: 'lantern-shop', price: 300 } },
  { id: 'tide-tapestry',   kind: 'decor-banner',     name: 'Tide Tapestry',   rarity: 3, source: { kind: 'lantern-shop', price: 300 } },

  { id: 'candle-cluster',  kind: 'decor-centerpiece', name: 'Candle Cluster', rarity: 1, source: { kind: 'embers-shop', price: 100 } },
  { id: 'wildflower-jar',  kind: 'decor-centerpiece', name: 'Wildflower Jar', rarity: 2, source: { kind: 'lantern-shop', price: 150 } },
  { id: 'dice-stack',      kind: 'decor-centerpiece', name: 'Dice Stack',     rarity: 2, source: { kind: 'lantern-shop', price: 150 } },

  { id: 'hop-wheat',       kind: 'decor-wreath',     name: 'Hop & Wheat',     rarity: 2, source: { kind: 'lantern-shop', price: 200 } },
  { id: 'winter-holly',    kind: 'decor-wreath',     name: 'Winter Holly',    rarity: 3, source: { kind: 'lantern-shop', price: 250 } },

  { id: 'moonlit-night',   kind: 'decor-window',     name: 'Moonlit Night',  rarity: 1, source: { kind: 'default' } },
  { id: 'snowfall',        kind: 'decor-window',     name: 'Snowfall',       rarity: 3, source: { kind: 'lantern-shop', price: 350 } },
  { id: 'harbor-lights',   kind: 'decor-window',     name: 'Harbor Lights',  rarity: 3, source: { kind: 'lantern-shop', price: 350 } },

  { id: 'oak-classic',     kind: 'decor-jukebox',    name: 'Oak Classic',    rarity: 2, source: { kind: 'default' } },
  { id: 'brass-deluxe',    kind: 'decor-jukebox',    name: 'Brass Deluxe',   rarity: 4, source: { kind: 'lantern-shop', price: 500 } },

  // ── companion (4) — hearth animals; shop-only, none default ──
  { id: 'tavern-cat',      kind: 'decor-companion',  name: 'Tavern Cat',     rarity: 2, source: { kind: 'embers-shop', price: 250 } },
  { id: 'old-hound',       kind: 'decor-companion',  name: 'Old Hound',      rarity: 2, source: { kind: 'embers-shop', price: 300 } },
  { id: 'snow-owl',        kind: 'decor-companion',  name: 'Snow Owl',       rarity: 3, source: { kind: 'lantern-shop', price: 400 } },
  { id: 'ember-fox',       kind: 'decor-companion',  name: 'Ember Fox',      rarity: 4, source: { kind: 'lantern-shop', price: 600 } },

  // ── "Your Own Tavern" surface styles (10) — floor / bar-counter / tables
  // slots. 3 defaults (the pre-existing hall look, `{kind:'default'}`) + 7
  // purchasable. Ids/rarities mirror shared/shell-3d/src/decor.ts's
  // SURFACE_STYLES + DECOR_ITEMS and shared/lobby/src/decor-registry.ts's
  // DECOR_INDEX — keep all three in sync.
  { id: 'oak-plank',    kind: 'decor-floor',       name: 'Oak Plank',    rarity: 1, source: { kind: 'default' } },
  { id: 'walnut-dark',  kind: 'decor-floor',       name: 'Walnut Dark',  rarity: 3, source: { kind: 'lantern-shop', price: 350 } },
  { id: 'stone-flag',   kind: 'decor-floor',       name: 'Stone Flag',   rarity: 4, source: { kind: 'lantern-shop', price: 500 } },
  { id: 'honey-pine',   kind: 'decor-floor',       name: 'Honey Pine',   rarity: 2, source: { kind: 'embers-shop', price: 250 } },

  { id: 'oak-bar',      kind: 'decor-bar-counter', name: 'Oak Bar',      rarity: 1, source: { kind: 'default' } },
  { id: 'marble-top',   kind: 'decor-bar-counter', name: 'Marble Top',   rarity: 4, source: { kind: 'lantern-shop', price: 500 } },
  { id: 'ebony-bar',    kind: 'decor-bar-counter', name: 'Ebony Bar',    rarity: 3, source: { kind: 'lantern-shop', price: 350 } },

  { id: 'oak-tables',   kind: 'decor-tables',      name: 'Oak Tables',   rarity: 1, source: { kind: 'default' } },
  { id: 'dark-set',     kind: 'decor-tables',      name: 'Dark Set',     rarity: 2, source: { kind: 'embers-shop', price: 300 } },
  { id: 'forest-set',   kind: 'decor-tables',      name: 'Forest Set',   rarity: 3, source: { kind: 'lantern-shop', price: 300 } },

  // ── "Your Own Tavern" Phase B — jukebox records (10) ──────────────────
  // Bare semantic ids match RECORDS in shared/lobby/src/records.ts (keep
  // ids/rarities in sync); catalog id is `record-${id}`, inventory bucket
  // `records`. 3 free defaults (everyone can request something), 5 cheap
  // embers-shop (listed in shop.ts's EMBERS_SHOP_RECORDS — validate-catalog
  // rule B/C), 2 achievement-earned. Achievement grants land via the
  // characters-lambda deriveInventory unlock() calls (mirrored in
  // leaderboards), same as other achievement cosmetics.
  { id: 'record-tap-anthem',      kind: 'record', name: 'Tap Anthem',      rarity: 1, source: { kind: 'default' } },
  { id: 'record-plinko-jangle',   kind: 'record', name: 'Plinko Jangle',   rarity: 1, source: { kind: 'default' } },
  { id: 'record-victory-march',   kind: 'record', name: 'Victory March',   rarity: 2, source: { kind: 'default' } },
  { id: 'record-hex-pulse',       kind: 'record', name: 'Hex Pulse',       rarity: 2, source: { kind: 'embers-shop', price: 200 } },
  { id: 'record-spire-ascent',    kind: 'record', name: 'Spire Ascent',    rarity: 3, source: { kind: 'embers-shop', price: 250 } },
  { id: 'record-cellar-groove',   kind: 'record', name: 'Cellar Groove',   rarity: 3, source: { kind: 'embers-shop', price: 250 } },
  { id: 'record-splatter-bop',    kind: 'record', name: 'Splatter Bop',    rarity: 2, source: { kind: 'embers-shop', price: 200 } },
  { id: 'record-coin-shuffle',    kind: 'record', name: 'Coin Shuffle',    rarity: 3, source: { kind: 'embers-shop', price: 300 } },
  { id: 'record-burnstile-drive', kind: 'record', name: 'Burnstile Drive', rarity: 4, source: { kind: 'achievement', id: 'streak-3' } },
  { id: 'record-draft-fanfare',   kind: 'record', name: 'Draft Fanfare',   rarity: 4, source: { kind: 'achievement', id: 'set-1' } },
];

/** Bundle SKUs. Phase E4 — 10 themed bundles at ~30-35% discount off the
 *  sum of their lantern-shop content prices. CRITICAL invariants enforced
 *  by `tools/validate-catalog.mjs`:
 *    1. Every `contents` id exists in COSMETIC_CATALOG.
 *    2. No bundle includes a `source.kind === 'season-premium'` item
 *       (would break Crimson-Solstice-exclusivity guarantee).
 *    3. Bundle price < sum(lantern-shop prices of contents).
 *
 *  Discount math (lantern-shop content sums shown in comments):
 *    - hearthkeeper:    sum 1450 → 950   (~34% off)
 *    - wraith-walker:   sum 1850 → 1250  (~32% off)
 *    - verdant-hollow:  sum 2400 → 1600  (~33% off)
 *    - brass-bone:      sum 1950 → 1300  (~33% off)
 *    - tavern-regular:  sum  850 → 550   (~35% off)
 *    - starry-night:    sum 3000 → 1950  (~35% off)
 *    - tinkers-toolkit: sum 1500 → 1000  (~33% off)
 *    - phoenix-court:   sum 1900 → 1250  (~34% off)
 *    - pebble-path:     sum 1000 → 650   (~35% off)
 *    - lantern-bearers: sum 1700 → 1150  (~32% off)
 *
 *  Theme reference: docs/phase-e-cosmetics.md §"Bundles (10)". Prices may
 *  drift slightly from the ballpark targets in the spec — the spec
 *  permits ±10% tweak when content cost forces it. The 30-40% discount
 *  invariant is the binding constraint. */
export const BUNDLES: BundleDef[] = [
  // Phase F-Foundation: bundle contents reference catalog ids that were
  // renamed from semantic to numeric form (`lantern-body` → `body-15`,
  // `candle-cap-hat` → `hat-14`, etc.). Border / background / accent ids
  // are unchanged. Phase F5 — repriced ~20% lower under the new common-
  // friendly pricing band, plus 5 new themed bundles featuring the new
  // seat-cosmetic + mask content. The Crimson Solstice five-piece + the
  // F5 premium-pass-only items (solstice-flame, forge-glow, runes,
  // crimson-veil) intentionally do NOT appear in any bundle —
  // premium-pass exclusivity is enforced by validate-catalog.mjs.
  {
    id: 'hearthkeeper-bundle',
    name: 'Hearthkeeper',
    contents: ['body-15', 'hat-14', 'accent-270'],
    price: 750,
    rarity: 3,
  },
  {
    id: 'wraith-walker-bundle',
    name: 'Wraith Walker',
    contents: ['body-16', 'face-11', 'gemstone-studded'],
    price: 1000,
    rarity: 3,
  },
  {
    id: 'verdant-hollow-bundle',
    name: 'Verdant Hollow',
    contents: ['body-12', 'hat-12', 'forest-grove'],
    price: 1300,
    rarity: 3,
  },
  {
    id: 'brass-bone-bundle',
    name: 'Brass & Bone',
    contents: ['body-17', 'hat-11', 'accent-270'],
    price: 1050,
    rarity: 3,
  },
  {
    id: 'tavern-regular-bundle',
    name: 'Tavern Regular',
    contents: ['face-9', 'accent-130'],
    price: 450,
    rarity: 2,
  },
  {
    id: 'starry-night-bundle',
    name: 'Starry Night',
    contents: ['starfield', 'runic-glyph'],
    price: 1550,
    rarity: 4,
  },
  {
    id: 'tinkers-toolkit-bundle',
    name: "Tinker's Toolkit",
    contents: ['hat-11', 'face-11', 'accent-130'],
    price: 800,
    rarity: 3,
  },
  {
    id: 'phoenix-court-bundle',
    name: 'Phoenix Court',
    contents: ['body-15', 'candle-circle', 'accent-270'],
    price: 1000,
    rarity: 4,
  },
  {
    id: 'pebble-path-bundle',
    name: 'Pebble Path',
    contents: ['body-18', 'accent-130', 'leaf-ring'],
    price: 500,
    rarity: 2,
  },
  {
    id: 'lantern-bearers-garb',
    name: "Lantern Bearer's Garb",
    contents: ['body-15', 'hat-14', 'face-9'],
    price: 900,
    rarity: 3,
  },

  // ── Phase F5 — new themed bundles (5) ──────────────────────────────
  // Each ~30-40% off the sum of its lantern-shop content prices.
  // forest-keeper:    body-12 (600) + seat-bg-garden (season-free; treat
  //                   as 600 retail) + seat-particle-petals (300 embers
  //                   ≈ 300 lanterns equivalent, but priced as if 350) =
  //                   ~1500 → 1000 (~33%).
  // wraith:           mask-phantom (achievement; 700 retail) +
  //                   seat-particle-shadows (achievement; 800 retail) +
  //                   body-16 (280) = ~1780 → 1100 (~38%).
  // gilded-court:     seat-border-gilded-rose (season-free; 1000 retail)
  //                   + mask-sunburst (1100) + seat-particle-coins (600)
  //                   = 2700 → 1750 (~35%).
  // tidewatcher:      seat-bg-tide-pool (season-free; 600 retail) +
  //                   seat-particle-bubbles (220) + seat-border-tide-foam
  //                   (season-free; 600 retail) = 1420 → 950 (~33%).
  // courtier:         body-12 (600) + face-9 (550) + accent-130 (220) =
  //                   1370 → 900 (~34%). A mid-tier "starter" featuring
  //                   only existing items. Replaces the doc's
  //                   solstice-set-bundle slot since premium-only items
  //                   cannot bundle.
  {
    id: 'forest-keeper-bundle',
    name: 'Forest Keeper',
    contents: ['body-12', 'seat-background-garden', 'seat-particle-petals'],
    price: 1000,
    rarity: 3,
  },
  {
    id: 'wraith-bundle',
    name: 'Wraith',
    contents: ['mask-phantom', 'seat-particle-shadows', 'body-16'],
    price: 1100,
    rarity: 4,
  },
  {
    id: 'gilded-court-bundle',
    name: 'Gilded Court',
    contents: ['seat-border-gilded-rose', 'mask-sunburst', 'seat-particle-coins'],
    price: 1750,
    rarity: 4,
  },
  {
    id: 'tidewatcher-bundle',
    name: 'Tidewatcher',
    contents: ['seat-background-tide-pool', 'seat-particle-bubbles', 'seat-border-tide-foam'],
    price: 950,
    rarity: 3,
  },
  {
    id: 'courtier-bundle',
    name: 'Courtier',
    contents: ['body-12', 'face-9', 'accent-130'],
    price: 900,
    rarity: 3,
  },

  // ── Themed FULL-SET bundles (4) — owner ask: "a suit of armor" ─────────
  // Unlike the grab-bag bundles above (2-3 items), each of these is a
  // complete, visually-coherent LOOK: outfit + hat + a matching themed
  // palette + an aura (avatar border) + a backdrop (avatar background). The
  // themed palette is the glue — cloth outfits (robe / cloak) tint to it in
  // 3D, and armor trim uses the palette accent, so every piece reads as one
  // set when equipped together. ~34-36% off the sum of individual
  // (lantern-equivalent) prices; a full 5-piece set sits above the grab-bag
  // bundles by design. Season-free / achievement / level pieces are treated
  // at their rarity-band retail equivalent in the sums (same convention the
  // F5 bundles above use). NONE are season-premium — bundle exclusivity holds
  // (validate-catalog.mjs enforces it).
  //
  // knight: outfit-plate (1100) + hat-15 Iron Helm (900) + palette-steel
  //         (550) + brass-frame aura (320) + tavern-hearth forge backdrop
  //         (320) = 3190 → 2100 (~34%). The steel palette tints the head +
  //         plate accent trim to match the helm; brass rivets + hearth glow
  //         complete the forge look.
  {
    id: 'knight-armory-bundle',
    name: "Knight's Armory",
    contents: ['outfit-plate', 'hat-15', 'palette-steel', 'brass-frame', 'tavern-hearth'],
    price: 2100,
    rarity: 4,
  },
  // arcane: outfit-robe (650) + hat-10 Witch Hat (~600) + palette-arcane
  //         (550) + runic-glyph aura (1000) + starfield backdrop (1000) =
  //         3800 → 2450 (~36%). The arcane palette turns the robe deep violet
  //         w/ cyan trim; runes + starfield frame the mage.
  {
    id: 'arcane-order-bundle',
    name: 'Arcane Order',
    contents: ['outfit-robe', 'hat-10', 'palette-arcane', 'runic-glyph', 'starfield'],
    price: 2450,
    rarity: 4,
  },
  // royal: outfit-cloak (~600) + hat-2 Crown (~650) + palette-royal (550) +
  //        gilded-rose-ring aura (~1000) + velvet-drape backdrop (600) =
  //        3400 → 2200 (~35%). The royal palette makes the cloak a purple-&-
  //        gold mantle matching the crown; gilt rose ring + velvet complete
  //        the throne look. (Non-premium regalia stand-in — the mythic
  //        Emberforged Regalia stays season-premium and un-bundleable.)
  {
    id: 'royal-court-bundle',
    name: 'Royal Court',
    contents: ['outfit-cloak', 'hat-2', 'palette-royal', 'gilded-rose-ring', 'velvet-drape'],
    price: 2200,
    rarity: 4,
  },
  // pirate: outfit-jerkin (~300) + hat-9 Tricorn (~600) + palette-naval (550)
  //         + tide-foam-ring aura (650) + tide-pool-wash backdrop (650) =
  //         2750 → 1800 (~35%). The naval palette gives navy-&-brass tones;
  //         leather jerkin + tricorn + tide framing read as a sea captain.
  {
    id: 'sea-captain-bundle',
    name: "Sea Captain's Regalia",
    contents: ['outfit-jerkin', 'hat-9', 'palette-naval', 'tide-foam-ring', 'tide-pool-wash'],
    price: 1800,
    rarity: 3,
  },

  // ── "Your Own Tavern" decor bundles (3) — owner ask, 2026-07-05 ────────
  // Sold in the Tavern Shop (web/src/features/tavern/TavernShop.tsx), not
  // the global lantern shop (DECOR_SHOP_LIVE gates the general listing,
  // not purchasability). Priced against the POST-cut prices above.
  //   witching-hour: witchfire (550) + midnight-star (300) + snowfall (350)
  //                  = 1200 → 850 (~29% off).
  {
    id: 'witching-hour-bundle',
    name: 'Witching Hour Set',
    contents: ['witchfire', 'midnight-star', 'snowfall'],
    price: 850,
    rarity: 4,
  },
  //   harvest-festival: harvest-braid (200) + hop-wheat (200) +
  //                     wildflower-jar (150) = 550 lantern-shop sum, PLUS
  //                     pennant-string (embers-shop, 100) thrown in at its
  //                     embers-equivalent — contents grant all 4 decor
  //                     items into their buckets, bundle itself is priced
  //                     in lanterns only. ~650 combined → 450 (~31% off).
  {
    id: 'harvest-festival-bundle',
    name: 'Harvest Festival Set',
    contents: ['harvest-braid', 'hop-wheat', 'wildflower-jar', 'pennant-string'],
    price: 450,
    rarity: 3,
  },
  //   harbor-night: tide-tapestry (300) + harbor-lights (350) +
  //                 classic-bottles (embers-shop, 150 embers-equivalent)
  //                 = 800 combined → 500 (~38% off).
  {
    id: 'harbor-night-bundle',
    name: 'Harbor Night Set',
    contents: ['tide-tapestry', 'harbor-lights', 'classic-bottles'],
    price: 500,
    rarity: 3,
  },
];

/** O(1) lookup. Built lazily so future Phase-E imports of an updated
 *  catalog rebuild on first call after module init. We don't expect
 *  catalog mutation post-init, so a simple Map is safe. */
let CATALOG_INDEX: Map<string, CosmeticDef> | null = null;

function index(): Map<string, CosmeticDef> {
  if (CATALOG_INDEX) return CATALOG_INDEX;
  const m = new Map<string, CosmeticDef>();
  for (const c of COSMETIC_CATALOG) m.set(c.id, c);
  CATALOG_INDEX = m;
  return m;
}

export function getCosmetic(id: string): CosmeticDef | null {
  return index().get(id) ?? null;
}

/** Day-of-year helper, UTC. Stable across clients — if all clients call
 *  `dailyFeatured(new Date())` within the same UTC date they see the
 *  same featured set. */
function utcDayOfYear(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.floor((date.getTime() - start) / 86400000);
}

/** Deterministic featured-shop subset for a given UTC date. Picks 4
 *  cosmetics from the lantern-shop pool. Returns [] if the pool is
 *  smaller than 1 (Phase A — empty catalog).
 *
 *  Uses a simple LCG seeded by `dayOfYear * 7 + year` so the sequence
 *  doesn't repeat across year boundaries. We sample WITHOUT replacement
 *  so a featured day never shows the same cosmetic twice. */
export function dailyFeatured(date: Date): CosmeticDef[] {
  const pool = cosmeticsForShop(date);
  if (pool.length === 0) return [];
  const featuredCount = Math.min(4, pool.length);
  const dayOfYear = utcDayOfYear(date);
  let seed = (dayOfYear * 7 + date.getUTCFullYear()) >>> 0;
  // Copy pool so we can splice without mutating the catalog.
  const remaining = [...pool];
  const out: CosmeticDef[] = [];
  for (let i = 0; i < featuredCount; i++) {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    const idx = seed % remaining.length;
    const picked = remaining.splice(idx, 1)[0];
    if (picked) out.push(picked);
  }
  return out;
}

/** Own Tavern decor visibility in the GLOBAL lantern/embers shops. Owner
 *  2026-07-05: decor gets its OWN shop surface inside the My Tavern editor
 *  (see web/src/features/tavern/TavernShop.tsx) instead of cluttering the
 *  general cosmetics shops — so this flips back to false. Purchases are
 *  UNAFFECTED: lambdas/shop's purchase() never calls cosmeticsForShop (only
 *  getShopState's DISPLAYED catalog does), so a lantern decor item is still
 *  buyable via POST /api/shop/purchase with its raw id regardless of this
 *  flag — the Tavern Shop calls that same endpoint directly. */
export const DECOR_SHOP_LIVE = false;

/** Filter helper — every cosmetic currently sellable in the lantern shop. */
export function cosmeticsForShop(_date: Date): CosmeticDef[] {
  // The `_date` argument is reserved for future date-gated catalog items
  // (seasonal sales, holidays). Phase A: all lantern-shop items are always
  // in the standing pool.
  return COSMETIC_CATALOG.filter(
    (c) => c.source.kind === 'lantern-shop' && (DECOR_SHOP_LIVE || !c.kind.startsWith('decor-')),
  );
}

/** Bucket the season-tagged cosmetics for a given season number. Used by
 *  the season-pass UI to show what's behind each tier on each track. */
export function cosmeticsForSeason(season: number): { free: CosmeticDef[]; premium: CosmeticDef[] } {
  const free: CosmeticDef[] = [];
  const premium: CosmeticDef[] = [];
  for (const c of COSMETIC_CATALOG) {
    if (c.source.kind === 'season-free' && c.source.season === season) free.push(c);
    else if (c.source.kind === 'season-premium' && c.source.season === season) premium.push(c);
  }
  return { free, premium };
}
